import { WatsonHealth3DMprToggle } from '@carbon/icons-react';
import { pipe } from 'fp-ts/lib/function';

import { Basket } from '@dora/vivian/dist/C_BasketView';
import { useTranslations } from '@dora/vivian/dist/utils/TranslationsWrapper';
import {
    ArModal,
    BasketView,
    BottomSelectionMenu,
    BrendaCanvas,
    CloseModal,
    LoadingScreen,
    MaterialMenu,
    ModConfigArModal,
    ReturnToStoreBanner,
    Toolbar,
} from '@dora/vivian/src';
import { Api, Fixtuur, ModularSofa, ServiceConfig, Services } from '@fixtuur/backpack';
import { Product as ProductService } from '@fixtuur/gift-client';
import { getModelUrl } from '@fixtuur/backpack/src/api/configuration/particle';
import { getModularConfiguratorById } from '@fixtuur/backpack/src/api/configuratorDefinition';
import { ModularFurniture } from '@fixtuur/brenda/src/renderer/modularFurniture';
import { Close } from '@mui/icons-material';
import { Box, Fab, useMediaQuery, useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Snackbar from '@mui/material/Snackbar';
import { Option as EffOption, Effect } from 'effect';
import * as O from 'fp-ts/Option';
import * as TO from 'fp-ts/TaskOption';
import * as Preact from 'preact';
import * as Hooks from 'preact/hooks';
import { AppControlContext } from '../contexts/appControl';
import { groupPropertyOptions } from '../data/modularConfiguration/optionGroups';
import {
    Modify,
    useModularConfiguration,
} from '../hooks/modularConfiguration/useModularConfiguration';
import { useModuleInteraction } from '../hooks/modularConfiguration/useModuleInteraction';
import { copyToClipboard, useModularSharing } from '../hooks/shared/useSharing';
import * as ArState from '../hooks/useAr/arState';
import { useAr } from '../hooks/useAr/useAr';
import { useGenerateModularQrUrl } from '../hooks/useAr/useAr/useGenerateQrUrl';
import { getFurnitureArModel } from '../hooks/useAr/useCurrentConfigurationFurnitureArModel';
import { useEffectful } from '../hooks/useEffectful';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { Cart, CheckoutLocation } from '../hooks/useParentApi';
import { useRenderer } from '../hooks/useRenderer';
import { useStellaLayer } from '../hooks/useStellaLayer';
import * as Actions from '../store/actions/app';
import * as BasketStore from '../store/basket';
import { useDispatch, useSelector } from '../store/redux';
import * as Selectors from '../store/selectors';

export interface Props {
    configuratorId: string;
    initialLoad: boolean;
    setInitialLoad: Hooks.StateUpdater<boolean>;
    isConfirmationModalOpen: boolean;
    closeConfirmationModal: () => void;
    closeApp: () => void;
    addToBasket?: (cart: Cart, from: CheckoutLocation) => void;
}

const brendaOptions = {
    camera: {
        panToModelOnSelection: false,
    },
    interactionHints: {
        camera: {
            active: true,
            image: 'assets/camera-nudge-hint.svg',
        },
    },
    environment: {
        skybox: { texture: 'assets/sofa-studio.env', rotationY: 3.9671, intensity: 1.1 },
    },
    antialias: true,
    handleResize: true,
    ssao: false,
    contrast: 1.6,
    exposure: 1.5,
};

export const ModularConfigurator = (p: Props) => {
    const { configuratorId, addToBasket } = p;
    const dispatch = useDispatch();

    const tenant = useSelector(Selectors.Core.tenant);
    const locale = useSelector(Selectors.Core.locale);
    const currency = useSelector(Selectors.Core.currency);
    const serviceConfig = useSelector(Selectors.Core.serviceConfig);
    const stellaLayer = useStellaLayer();

    const showProductDimensions = useFeatureFlag('show-product-dimensions');
    const removePricingEnabled = useFeatureFlag('remove-pricing-enabled');
    const conversionId = useSelector(Selectors.ConversionId.conversionId);
    const shouldOpenArModal = useSelector(Selectors.Initialisation.shouldShowArModal);

    const logoSrc = useSelector(Selectors.Core.logoSrc);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));

    const [openMaterialMenu, setOpenMaterialMenu] = Hooks.useState<boolean>(false);
    const [isBasketViewActive, setIsBasketViewActive] = Hooks.useState<boolean>(false);
    const [showDimensions, setShowDimensions] = Hooks.useState<boolean>(false);
    const [showCopyAlert, setShowCopyAlert] = Hooks.useState<boolean>(false);
    const [isArModalActive, setArModalActive] = Hooks.useState<boolean>(false);
    const [errors, setErrors] = Hooks.useState<string[]>([]);

    const errorMessages: Record<Services.ModularTemplates.FinalisationRule, string> = {
        validateCornersConnected: 'You must add a seat to the corner before heading to checkout',
    };

    const configuratorDefinition = useEffectful(
        () =>
            Effect.Do.pipe(
                Effect.bind('tenant', () => Effect.fromNullable(tenant)),
                Effect.bind('locale', () => Effect.fromNullable(locale)),
                Effect.flatMap(({ tenant, locale }) =>
                    getModularConfiguratorById(tenant, configuratorId, locale),
                ),
            ),
        [tenant, locale, configuratorId],
    ).result;

    const modularTemplate = useEffectful(
        () =>
            Effect.Do.pipe(
                Effect.bind('tenant', () => Effect.fromNullable(tenant)),
                Effect.bind(
                    'modularTemplates',
                    () => Services.ModularTemplates.ModularTemplateService,
                ),
                Effect.bind('configuratorDefinition', () =>
                    Effect.fromNullable(configuratorDefinition),
                ),
                // TODO: Configurator ID is not necessarily modular template id - for now assume 1:1 correspondence
                Effect.flatMap(({ modularTemplates, tenant, configuratorDefinition }) =>
                    modularTemplates.getModularOptionsById(
                        tenant,
                        configuratorDefinition.modularTemplateId,
                    ),
                ),
                Effect.provide(stellaLayer),
            ),
        [tenant, configuratorDefinition],
    ).result;

    const startingSelections = useSelector(Selectors.Initialisation.modularSelections);

    const {
        pieces,
        globalSelections,
        globalPropertyOptions,
        modify: modifyPieces,
        checkValidPartialBuild,
        finalValidationErrors,
        canInsertAt,
        updateGlobalSelection,
        availableModules,
        undo: undoConfiguration,
        redo: redoConfiguration,
    } = useModularConfiguration(
        modularTemplate,
        startingSelections?.globalSelections,
        startingSelections?.pieceSelections,
    );

    Hooks.useEffect(() => {
        if (!finalValidationErrors.length) setErrors([]);
    }, [finalValidationErrors]);

    const openBasketView = () => {
        if (finalValidationErrors.length) {
            setErrors(finalValidationErrors.map(error => errorMessages[error]));
        } else {
            setIsBasketViewActive(true);
            dispatch(Actions.viewBasketAction());
            if (!isSmallScreen) {
                openArModal();
            }
        }
    };

    const availableProducts = useEffectful(
        () =>
            Effect.gen(function* () {
                const tenantId = yield* Effect.fromNullable(tenant);
                const productService = yield* ProductService.ProductService;

                const products = yield* Effect.all(
                    Object.values(availableModules)
                        .flat()
                        .map(moduleOption =>
                            productService.getById(moduleOption.properties['Product'], tenantId),
                        ),
                );

                return products.map(Fixtuur.Product.fromApi);
            }).pipe(Effect.provide(stellaLayer)),
        [availableModules, tenant, stellaLayer],
    ).result;

    const placedPieces = Hooks.useMemo(
        () =>
            modularTemplate ? ModularSofa.placePieceSequence(pieces, modularTemplate) : undefined,
        [pieces, modularTemplate],
    );

    const { selectedPiece, selectPiece, deselectPiece, interactions, pieceUpdate } =
        useModuleInteraction(placedPieces ?? [], canInsertAt, checkValidPartialBuild, modifyPieces);

    const productBasedDimensions = Hooks.useMemo(() => {
        if (!modularTemplate) return null;
        const canonicalPlaced = ModularSofa.placePieceSequence(pieces, modularTemplate, true);
        const canonicalDims = ModularSofa.calculatePlacedDimensions(canonicalPlaced);

        return { width: canonicalDims[0], height: canonicalDims[1], depth: canonicalDims[2] };
    }, [pieces, modularTemplate]);

    const undo =
        undoConfiguration &&
        (() => {
            deselectPiece();
            dispatch(Actions.undoAction());
            undoConfiguration();
        });
    const redo =
        redoConfiguration &&
        (() => {
            deselectPiece();
            dispatch(Actions.redoAction());
            redoConfiguration();
        });

    const renderable = Hooks.useMemo(
        (): ModularFurniture | undefined =>
            serviceConfig &&
            placedPieces &&
            createFurnitureForPlacedPieces(serviceConfig, placedPieces, selectedPiece),
        [placedPieces, serviceConfig, selectedPiece],
    );

    const onFurnitureModuleClick = (_furnitureId: string, moduleIndex: number) => {
        selectPiece(moduleIndex);
    };
    const onSceneClick = () => {
        deselectPiece();
    };

    const sceneButtons = Hooks.useMemo(
        () =>
            interactions.map(interaction => {
                const { kind, ...buttonInfo } = interaction;
                const buttonDisplay =
                    kind === 'insert'
                        ? { text: '+', image: 'assets/modularButtons/add.svg' }
                        : { text: '-', image: 'assets/modularButtons/remove.svg' };
                return { ...buttonDisplay, ...buttonInfo };
            }),
        [interactions],
    );

    const {
        rendererOptions,
        onRendererReady,
        takeSnapshot,
        loading: rendererLoading,
        modelGeneration,
    } = useRenderer(
        renderable,
        showDimensions,
        showProductDimensions && productBasedDimensions ? productBasedDimensions : undefined,
        { onFurnitureModuleClick, onSceneClick },
        sceneButtons,
        brendaOptions,
    );

    Hooks.useEffect(() => {
        if (rendererLoading === false) {
            setTimeout(() => {
                p.setInitialLoad(false);
                dispatch(Actions.loadingFinishedAction());
            }, 2000);
        }
    }, [rendererLoading]);

    const snapshot = useEffectful(
        () => (!rendererLoading ? takeSnapshot : EffOption.none()),
        [takeSnapshot, rendererLoading],
    ).result;

    const onDimensionsToggle = () => {
        setShowDimensions(!showDimensions);
        dispatch(
            Actions.dimensionsToggledAction({
                isVisible: showDimensions,
            }),
        );
    };

    const configurableProperties =
        serviceConfig &&
        configuratorDefinition &&
        Object.fromEntries(
            Object.entries(configuratorDefinition.globalConfigurableProperties).map(
                ([propertyName, displayInfo]): [string, MaterialMenu.GlobalProperty[]] => {
                    const propertyOptions = globalPropertyOptions?.[propertyName] ?? [];
                    const optionGroups = groupPropertyOptions(
                        displayInfo.groups ?? [],
                        propertyOptions,
                    );

                    return [
                        propertyName,
                        optionGroups.map(([group, options]) => ({
                            id: propertyName,
                            name: group,
                            minPrice: '',
                            options: options.map(value => ({
                                id: `${value}`,
                                displayName:
                                    group && typeof value === 'string'
                                        ? value.replace(group, '')
                                        : value,
                                swatch: `${serviceConfig.models}/thumbnails/swatches/${value}.jpg`,
                            })),
                        })),
                    ];
                },
            ),
        );

    const primaryProperty = 'fabric';

    const [globalSelectionTab, setGlobalSelectionTab] = Hooks.useState(primaryProperty);

    const properties = configurableProperties?.[globalSelectionTab];

    const activeSwatch =
        configurableProperties?.[primaryProperty]
            .flatMap(m => m.options)
            .find(option => option.id === globalSelections[primaryProperty])?.swatch ??
        'assets/swatch.png';

    const selectedOption =
        pieceUpdate !== null && pieceUpdate.replacePiece !== null
            ? pieces[pieceUpdate.replacePiece]
            : undefined;

    const addPieceOptions = Hooks.useMemo(
        () =>
            Object.entries(availableModules)
                .flatMap(([type, options]) =>
                    options.map((option): [string, Services.ModularTemplates.ModuleOption] => [
                        type,
                        option,
                    ]),
                )
                .map(([moduleType, moduleOption]) => {
                    const product = availableProducts?.find(
                        p => Fixtuur.Product.id(p) === moduleOption.properties['Product'],
                    );
                    const image =
                        (product && Fixtuur.Product.image(product)) ?? 'assets/swatch.png';

                    return {
                        id: moduleOption.particleId,
                        image,
                        type: moduleType,
                    };
                }),
        [availableProducts, availableModules],
    );

    const validPieceOptions = Hooks.useMemo(
        () =>
            addPieceOptions.filter(option => {
                if (!pieceUpdate) return false;
                const modification: Modify = pieceUpdate.modification(option.id);
                return checkValidPartialBuild(modification);
            }),
        [addPieceOptions, pieceUpdate],
    );

    const basket: Basket = Hooks.useMemo(() => {
        const items =
            availableProducts
                ?.map(product => {
                    const moduleIds = Object.values(availableModules)
                        .flat()
                        .filter(
                            moduleOption =>
                                moduleOption.properties.Product === Fixtuur.Product.id(product),
                        )
                        .map(moduleOption => moduleOption.particleId);

                    return {
                        id: Fixtuur.Product.id(product),
                        pieceThumbnail: Fixtuur.Product.image(product),
                        pieceName: Fixtuur.Product.name(product),
                        piecePrice: Fixtuur.Product.price(product),
                        pieceOriginalPrice: pipe(
                            Fixtuur.Product.originalPrice(product),
                            O.getOrElse(() => Fixtuur.Product.price(product)),
                        ),
                        pieceCount: pieces.filter(pieceId => moduleIds.includes(pieceId)).length,
                    };
                })
                .filter(({ pieceCount }) => pieceCount > 0) ?? [];

        const total = items.reduce((next, basketItem) => {
            return (next = next + basketItem.piecePrice * basketItem.pieceCount);
        }, 0);

        const originalTotal = items.reduce((next, basketItem) => {
            return (next = next + basketItem.pieceOriginalPrice * basketItem.pieceCount);
        }, 0);

        return { items, total, originalTotal };
    }, [pieces, availableModules, availableProducts]);

    Hooks.useEffect(() => {
        dispatch(
            BasketStore.update({
                products: basket.items.map(item => ({
                    productId: item.id,
                    quantity: item.pieceCount,
                    unitPrice: item.piecePrice,
                })),
                price: basket.total,
                currency: currency ?? 'GBP',
            }),
        );
    }, [basket]);

    const checkout =
        !removePricingEnabled && addToBasket && basket && conversionId
            ? (from: CheckoutLocation) => {
                  addToBasket(
                      {
                          conversionId,
                          price: basket.total,
                          products: basket.items.map(
                              ({ id, pieceCount }: { id: string; pieceCount: number }) => ({
                                  id,
                                  quantity: pieceCount,
                              }),
                          ),
                      },
                      from,
                  );
                  p.closeApp();
              }
            : undefined;

    const generateQrUrl = useGenerateModularQrUrl(configuratorId, pieces, globalSelections);
    const getModelUrl = Hooks.useMemo(
        () =>
            !serviceConfig
                ? TO.none
                : getFurnitureArModel(
                      serviceConfig,
                      modelGeneration.gltfBlob,
                      modelGeneration.modelJson,
                  ),
        [modelGeneration],
    );

    const { closeArModal, openArModal, arState } = useAr({
        onCheckout: checkout ? () => checkout('ar') : undefined,
        delayLoad: true,
        placement: 'floor',
        getModelUrl,
        isAppLaunching: p.initialLoad,
        generateQrUrl,
    });

    const { sharingUrl, openSharingModal, closeSharingModal } = useModularSharing(
        configuratorId,
        pieces,
        globalSelections,
    );

    Hooks.useEffect(() => {
        if (sharingUrl() && p.isConfirmationModalOpen) dispatch(Actions.shareLinkLoadedAction());
    }, [p.isConfirmationModalOpen, sharingUrl()]);

    Hooks.useEffect(() => {
        if (p.isConfirmationModalOpen) {
            openSharingModal();
        } else {
            closeSharingModal();
        }
    }, [p.isConfirmationModalOpen]);

    const propertyTabs = configuratorDefinition
        ? Object.entries(configuratorDefinition.globalConfigurableProperties).map(
              ([propertyName, displayInfo]) => ({
                  id: propertyName,
                  header: displayInfo.displayName,
              }),
          )
        : [];

    const moduleSections: Api.ConfiguratorDefinition.ModuleSection[] = Hooks.useMemo(
        () => [
            { displayName: 'all', moduleTypes: 'all' },
            { displayName: 'middle', moduleTypes: ['middle'] },
            { displayName: 'corner', moduleTypes: ['connecting-corner'] },
            {
                displayName: 'end',
                moduleTypes: [
                    'left-end',
                    'left-end-chaise',
                    'right-end',
                    'right-end-chaise',
                    'ottoman',
                ],
            },
        ],
        [],
    );

    const moduleTabs = Hooks.useMemo(
        () =>
            moduleSections.map((sectionInfo, i) => ({
                header: sectionInfo.displayName,
                id: i.toString(),
                types:
                    sectionInfo.moduleTypes !== 'all'
                        ? sectionInfo.moduleTypes
                        : Services.ModularTemplates.ModuleType.literals,
            })),
        [moduleSections],
    );

    return (
        <Box
            sx={{
                display: 'flex',
                position: 'relative',
                flexDirection: 'column',
                width: '100%',
                height: '100%',
                borderRadius: { xs: '0px', md: '24px' },
                overflow: 'hidden',
            }}
        >
            <LoadingScreen.Component
                show={p.initialLoad}
                logoUrl={logoSrc}
                loadingImage={`assets/loadingImages/${tenant}.png`}
                configuratorType="sofa"
                optionType="seat"
            />
            <BasketView.Component
                sofaSnapshot={snapshot}
                snapshotLoading={snapshot === undefined}
                show={isBasketViewActive}
                onBasketViewClose={() => {
                    setIsBasketViewActive(false);
                    closeArModal();
                }}
                basket={basket}
                linkProps={{ ...ArState.toArModalProps(arState) }}
                onViewInRoomClick={() => {
                    openArModal();
                    dispatch(Actions.viewInRoomAction());
                }}
                onAddToBasket={
                    checkout
                        ? () => {
                              dispatch(Actions.addToBasketAction());
                              checkout('explore');
                          }
                        : undefined
                }
            />
            <Toolbar.Component
                showDimensions={showDimensions}
                onDimensionsToggle={onDimensionsToggle}
                activeSwatch={activeSwatch}
                onSwatchClick={() => {
                    deselectPiece();
                    setOpenMaterialMenu(true);
                    dispatch(Actions.openSelectionsMenuAction());
                }}
                basketPrice={`${Intl.NumberFormat('en-GB', {
                    style: 'currency',
                    currency: currency ?? 'GBP',
                }).format(basket.total)}`}
                onBasketClick={openBasketView}
                onUndoClick={undo}
                onRedoClick={redo}
                isPieceLimitReached={placedPieces?.length === 12}
                sx={{
                    top: isSmallScreen ? '17px' : '20px',
                    left: 0,
                    right: 0,
                    margin: 'auto',
                }}
            />
            <BrendaCanvas.Component options={rendererOptions} onReady={onRendererReady} />
            {placedPieces?.length && placedPieces.length >= 3 && (
                <Button
                    variant="contained"
                    color="secondary"
                    size="large"
                    startIcon={<WatsonHealth3DMprToggle size="24" />}
                    sx={{
                        position: 'absolute',
                        left: '0%',
                        right: '0%',
                        margin: 'auto',
                        bottom: '56px',
                        width: '191px',
                        backgroundColor: '#F5F5F5 !important',
                        color: 'black !important',
                        boxShadow: '0 4px 80px rgba(0,0,0,0.15) !important',
                        borderRadius: '100px',
                        paddingLeft: '16px',
                        height: '50px',
                        fontWeight: 600,
                    }}
                    onClick={() => {
                        setArModalActive(true);
                        dispatch(Actions.viewInRoomAction());
                        openArModal();
                    }}
                >
                    View in your room
                </Button>
            )}
            {
                <ModConfigArModal.Component
                    open={isArModalActive && !isSmallScreen}
                    onCloseClick={() => {
                        setArModalActive(false);
                        closeArModal();
                    }}
                    {...ArState.toArModalProps(arState)}
                />
            }
            {isSmallScreen && (isArModalActive || isBasketViewActive || shouldOpenArModal) && (
                <ArModal.Component
                    // TODO: provide helpers for checking these things
                    open={ArState.isOpen(arState)}
                    title="See this item in your home" // TODO: i18n
                    onCloseClick={closeArModal}
                    sx={{
                        right: '20px',
                        top: '88px',
                    }}
                    {...ArState.toArModalProps(arState)}
                />
            )}
            <MaterialMenu.Component
                open={openMaterialMenu}
                loading={rendererLoading}
                selectedGlobalOption={globalSelections[globalSelectionTab]}
                onCloseClick={() => setOpenMaterialMenu(false)}
                onOptionClick={value => {
                    dispatch(
                        Actions.changeSelectionAction({
                            newSelections: {
                                [globalSelectionTab]: value,
                            },
                        }),
                    );
                    updateGlobalSelection(globalSelectionTab, value);
                }}
                properties={properties ?? []}
                activeTab={globalSelectionTab}
                tabProps={
                    propertyTabs.length > 1
                        ? {
                              tabs: propertyTabs,
                              onTabClick: setGlobalSelectionTab,
                          }
                        : undefined
                }
                drawerSx={{
                    top: { xs: ReturnToStoreBanner.RETURN_TO_STORE_HEIGHT, md: 0 },
                    height: {
                        xs: `calc(100% - ${ReturnToStoreBanner.RETURN_TO_STORE_HEIGHT})`,
                        md: '100%',
                    },
                }}
            />
            <BottomSelectionMenu.Component
                open={pieceUpdate !== null}
                optionsLoading={rendererLoading}
                tabs={moduleTabs}
                options={validPieceOptions}
                onTabChange={(id: string) => {
                    dispatch(
                        Actions.viewCatalogueTabAction({
                            catalogueTab: id,
                        }),
                    );
                }}
                onCatalogueStateChange={(id: string, isOpen: boolean) => {
                    dispatch(
                        Actions.viewCataloguePanelAction({
                            catalogueTab: id,
                            isOpen: isOpen,
                        }),
                    );
                }}
                selectedOption={selectedOption}
                onSelect={pieceUpdate?.apply ?? (() => {})}
            />
            <CloseModal.Component
                open={p.isConfirmationModalOpen}
                onCloseClick={() => {
                    p.closeConfirmationModal();
                    dispatch(Actions.cancelCloseFromCloseModalAction());
                    setShowCopyAlert(false);
                }}
                onCloseAppClick={() => {
                    dispatch(Actions.closeFromCloseModalAction());
                    p.closeApp();
                }}
                link={sharingUrl()}
                onCopyClick={(link: string) => {
                    copyToClipboard(link);
                    dispatch(Actions.shareLinkCopiedAction());
                    setShowCopyAlert(true);
                }}
            />
            <Snackbar
                open={showCopyAlert}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                sx={{
                    top: '90px !important',
                    position: 'absolute',
                    width: 'fit-content',
                    left: 0,
                    right: 0,
                    margin: 'auto',
                }}
            >
                <Alert>Link copied to clipboard</Alert>
            </Snackbar>
            {errors.map(error => (
                <Snackbar
                    open={error !== undefined}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    sx={{
                        top: '90px !important',
                        position: 'absolute',
                        width: 'max-content',
                        left: 0,
                        right: 0,
                        margin: 'auto',
                    }}
                >
                    <Alert severity="error" variant="filled" color="error">
                        {error}
                    </Alert>
                </Snackbar>
            ))}
        </Box>
    );
};

export const Component = (props: {
    configuratorId: string;
    addToBasket?: (cart: Cart, from: CheckoutLocation) => void;
}) => {
    const theme = useTheme();
    const { t } = useTranslations();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('md'));
    const appControl = Hooks.useContext(AppControlContext);
    const [isCloseModalOpen, setCloseModalOpen] = Hooks.useState<boolean>(false);
    const [initialLoad, setInitialLoad] = Hooks.useState<boolean>(true);
    const dispatch = useDispatch();

    const closeApp = initialLoad
        ? () => appControl?.closeApp()
        : () => {
              setCloseModalOpen(true);
              dispatch(Actions.closeModalOpenedAction());
          };

    return (
        <Box
            width="100%"
            height="100%"
            display="flex"
            justifyContent="start"
            overflow="hidden"
            flexDirection={isSmallScreen ? 'column' : 'row'}
        >
            {isSmallScreen && (
                <ReturnToStoreBanner.Component
                    onReturnToStoreClick={closeApp}
                    returnToStoreText={t('nav.return')}
                />
            )}

            <ModularConfigurator
                configuratorId={props.configuratorId}
                initialLoad={initialLoad}
                setInitialLoad={setInitialLoad}
                isConfirmationModalOpen={isCloseModalOpen}
                closeConfirmationModal={() => setCloseModalOpen(false)}
                closeApp={() => appControl?.closeApp()}
                addToBasket={props.addToBasket}
            />

            {!isSmallScreen && (
                <Fab
                    size="medium"
                    sx={{
                        backgroundColor: '#f5f5f5',
                        marginLeft: '24px',
                        flex: 'none',
                    }}
                    onClick={closeApp}
                >
                    <Close color="secondary" />
                </Fab>
            )}
        </Box>
    );
};

function createFurnitureForPlacedPieces(
    serviceConfig: ServiceConfig.ServiceConfig,
    placedPieces: ModularSofa.PlacedPiece[],
    selectedPiece: number | null,
): ModularFurniture {
    return {
        _tag: 'ModularFurniture',
        position: { x: 0, y: 0, z: 0 },
        instanceId: 'modular-sofa',
        pieces: placedPieces.map(placedPiece => {
            const [x, y, z] = placedPiece.position.value();
            const [qw, qx, qy, qz] = placedPiece.orientation.quaternion();
            const modelUrl = getModelUrl(serviceConfig, { id: placedPiece.particleId });
            return {
                id: placedPiece.particleId,
                modelUrl,
                position: { x, y, z },
                orientation: { w: qw, x: qx, y: qy, z: qz },
            };
        }),
        selectedPiece: selectedPiece,
    };
}
